import React, { useEffect, useState } from 'react';
import { Coffee, MapPin, Clock, Instagram, Facebook, Star, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { branches, socialMedia, contactInfo, aboutContent } from '../mockData';

const Home = () => {
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="home-container">
      {/* Navigation Header */}
      <nav className={`main-nav ${scrollY > 50 ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <img 
            src="https://customer-assets.emergentagent.com/job_6d3d1c27-53ba-471d-84f4-6f82adc27435/artifacts/1cmozmwp_Check%20Black.png" 
            alt="Checkout Coffee" 
            className="nav-logo"
          />
          <div className="nav-links">
            <a href="#about" className="nav-link">About</a>
            <a href="#branches" className="nav-link">Locations</a>
            <a href="#hours" className="nav-link">Hours</a>
            <a href="#instagram" className="nav-link">Social</a>
          </div>
          <Button className="nav-cta">
            <MapPin className="icon-small" />
            Find Us
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-blur-overlay"></div>
        </div>
        <div className="hero-content">
          <div className="hero-logo-container">
            <img 
              src="https://customer-assets.emergentagent.com/job_6d3d1c27-53ba-471d-84f4-6f82adc27435/artifacts/haaa568w_Checkout%20White.png" 
              alt="Checkout Coffee" 
              className="hero-logo"
            />
          </div>
          <h1 className="hero-title">Premium Coffee Experience</h1>
          <p className="hero-subtitle">Expertly crafted beverages in the heart of Doha's finest malls</p>
          <div className="hero-cta-group">
            <Button className="cta-primary">
              <MapPin className="icon" />
              Find a Branch
            </Button>
            <Button className="cta-secondary">
              <Phone className="icon" />
              Get Directions
            </Button>
          </div>
        </div>
        <div className="scroll-indicator">
          <div className="scroll-line"></div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section" id="about" data-animate>
        <div className="section-container">
          <div className="about-content">
            <div className="section-badge">
              <Coffee className="badge-icon" />
              <span>Our Story</span>
            </div>
            <h2 className="section-title">{aboutContent.heading}</h2>
            <p className="section-description">{aboutContent.description}</p>
          </div>
          <div className="features-grid">
            {aboutContent.features.map((feature, index) => (
              <Card key={index} className="feature-card">
                <CardContent className="feature-content">
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-description">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Branches Section */}
      <section className="branches-section" id="branches" data-animate>
        <div className="section-container">
          <div className="section-header">
            <div className="section-badge">
              <MapPin className="badge-icon" />
              <span>Our Locations</span>
            </div>
            <h2 className="section-title">Visit Us in Doha</h2>
            <p className="section-subtitle">Find your nearest Checkout Coffee location</p>
          </div>
          <div className="branches-grid">
            {branches.map((branch) => (
              <Card key={branch.id} className={`branch-card ${branch.status === 'coming-soon' ? 'coming-soon' : ''}`}>
                <CardContent className="branch-content">
                  {branch.status === 'coming-soon' && (
                    <Badge className="coming-soon-badge">Opening Soon</Badge>
                  )}
                  <h3 className="branch-name">{branch.name}</h3>
                  <p className="branch-address">{branch.address}</p>
                  <div className="branch-hours">
                    <Clock className="icon-small" />
                    <span>{branch.hours}</span>
                  </div>
                  {branch.status === 'open' && (
                    <Button 
                      className="branch-cta"
                      onClick={() => window.open(branch.mapUrl, '_blank')}
                    >
                      <MapPin className="icon-small" />
                      Get Directions
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Opening Hours Section */}
      <section className="hours-section" id="hours" data-animate>
        <div className="section-container">
          <div className="hours-content">
            <Clock className="hours-icon" />
            <h2 className="section-title">Opening Hours</h2>
            <div className="hours-display">
              <p className="hours-days">Monday - Sunday</p>
              <p className="hours-time">7:00 AM - 12:00 AM</p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="social-proof-section" id="reviews" data-animate>
        <div className="section-container">
          <div className="section-badge">
            <Star className="badge-icon" />
            <span>Customer Reviews</span>
          </div>
          <h2 className="section-title">What Our Customers Say</h2>
          <div className="reviews-placeholder">
            <Star className="star-icon" />
            <p className="placeholder-text">Be the first to review Checkout Coffee!</p>
            <p className="placeholder-subtext">Share your experience with us on social media</p>
          </div>
        </div>
      </section>

      {/* Instagram Section */}
      <section className="instagram-section" id="instagram" data-animate>
        <div className="section-container">
          <div className="section-badge">
            <Instagram className="badge-icon" />
            <span>Follow Us</span>
          </div>
          <h2 className="section-title">Join Our Community</h2>
          <p className="section-subtitle">Follow us on Instagram for daily inspiration and exclusive offers</p>
          <div className="social-links">
            <Button 
              className="social-button instagram"
              onClick={() => window.open(socialMedia.instagram, '_blank')}
            >
              <Instagram className="icon" />
              @thatcheckoutcoffee
            </Button>
            <Button 
              className="social-button facebook"
              onClick={() => window.open(socialMedia.facebook, '_blank')}
            >
              <Facebook className="icon" />
              Checkout Coffee
            </Button>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section" id="cta" data-animate>
        <div className="cta-content">
          <h2 className="cta-title">Grab Your Perfect Cup Today</h2>
          <p className="cta-subtitle">Visit your nearest Checkout Coffee and experience premium quality</p>
          <div className="cta-buttons">
            <Button className="cta-primary-large">
              <MapPin className="icon" />
              Find a Branch
            </Button>
            <Button className="cta-secondary-large">
              <Phone className="icon" />
              Call Us Now
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <img 
              src="https://customer-assets.emergentagent.com/job_6d3d1c27-53ba-471d-84f4-6f82adc27435/artifacts/1cmozmwp_Check%20Black.png" 
              alt="Checkout Coffee" 
              className="footer-logo"
            />
            <p className="footer-tagline">Premium Coffee Experience in Doha</p>
          </div>
          <div className="footer-section">
            <h4 className="footer-heading">Quick Links</h4>
            <ul className="footer-links">
              <li><a href="#about">About Us</a></li>
              <li><a href="#branches">Locations</a></li>
              <li><a href="#hours">Opening Hours</a></li>
              <li><a href="#instagram">Social Media</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4 className="footer-heading">Contact</h4>
            <div className="footer-contact">
              <div className="contact-item">
                <Mail className="icon-small" />
                <span>{contactInfo.email}</span>
              </div>
            </div>
          </div>
          <div className="footer-section">
            <h4 className="footer-heading">Follow Us</h4>
            <div className="footer-social">
              <a href={socialMedia.instagram} target="_blank" rel="noopener noreferrer" className="social-icon">
                <Instagram />
              </a>
              <a href={socialMedia.facebook} target="_blank" rel="noopener noreferrer" className="social-icon">
                <Facebook />
              </a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2025 Checkout Coffee. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
